import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import cashReservesService from '../services/cashReservesService';
import ConsolidatedPanel from '../components/cashReserves/ConsolidatedPanel';
import IgnoreListManager from '../components/cashReserves/IgnoreListManager';
import HiddenSuggestionsModal from '../components/cashReserves/HiddenSuggestionsModal';
import ReserveRequestQueue from '../components/cashReserves/ReserveRequestQueue';
import RmdClientsTab from '../components/cashReserves/RmdClientsTab';
import SortableHeader from '../components/cashReserves/SortableHeader';
import TamaracTodoList from '../components/cashReserves/TamaracTodoList';
import { sortAccounts } from '../components/cashReserves/AccountsSubtable';
import { clearFeeSnapshotCache } from '../components/cashReserves/feeSnapshotCache';
import { clearReserveRequestOptionsCache } from '../components/cashReserves/reserveRequestOptionsCache';
import { clearTxCache, invalidateTxCache } from '../components/cashReserves/txCache';
import {
  DEFAULT_DAYS,
  FAMILY_SORT_ACCESSORS,
  accountMissingManagementFee,
  buildFamilies,
  familyIdOf,
  fmtCurrency,
  hasNonRecurringReserve,
  isFirmAccount,
  isManagementFeeOnly,
  isRecurringFlagAlert,
  managementFeeDollars,
  showsNeedsAttention,
  showsNeedsRereview,
  sortByColumn,
} from '../components/cashReserves/helpers';

/**
 * CashReservesDashboard — account families that have a configured cash reserve.
 *
 * A family expands once to reveal everything about it: its accounts, their
 * merged reserves, suggested actions and recent transactions. Ungrouping does
 * not change that detail view — each account simply becomes a family of one, so
 * both modes render through the same components and cannot drift apart.
 */

// ─── family row ───────────────────────────────────────────────────────────────
const FamilyRow = ({
  family,
  expanded,
  onToggle,
  days,
  missingManagementFee,
  matchCount,
  matchesFilters,
  sortConfig,
  onSort,
  onReviewed,
  onIgnored,
  onAnnotationChanged,
  onApplied,
  onCombined,
  onAlertIgnoresChanged,
  selectedSuggestedActions,
  ignoreManagementFee,
  reloadToken,
}) => {
  const sortedAccounts = sortAccounts(family.accounts, sortConfig);
  const solo = family.account_count === 1;
  const missingValue = family.accounts_missing_value || 0;
  return (
    <>
      <tr
        style={{ cursor: 'pointer' }}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td>
          <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted me-2`}></i>
          <i className={`bi ${solo ? 'bi-person' : 'bi-people-fill'} text-primary me-2`}></i>
          <span className="fw-semibold" title={family.family_full_name}>
            {family.family_name}
          </span>
          {family.has_potential_duplicates && (
            <i
              className="bi bi-exclamation-diamond-fill text-warning ms-2"
              title="Potential duplicate reserves in this family"
            ></i>
          )}
          {missingManagementFee && (
            <span
              className="badge bg-warning text-dark ms-2"
              title="At least one account in this family is missing a Management Fee cash reserve"
            >
              <i className="bi bi-cash-coin me-1"></i>
              No mgmt fee
            </span>
          )}
        </td>
        <td className="text-center">
          <span className="badge bg-primary">{family.account_count}</span>
        </td>
        <td>
          {family.alert_level === 'red' ? (
            <span className="badge bg-danger" title={family.alert_labels?.join(', ') || undefined}>
              <i className="bi bi-exclamation-triangle-fill me-1"></i>
              Needs Attention ({family.needs_attention_count})
            </span>
          ) : family.rereview_count > 0 ? (
            <span className="badge bg-warning text-dark">
              <i className="bi bi-arrow-repeat me-1"></i>
              Re-review ({family.rereview_count})
            </span>
          ) : family.alert_level === 'orange' ? (
            <span className="badge bg-warning text-dark" title={family.alert_labels?.join(', ') || undefined}>
              <i className="bi bi-lightbulb me-1"></i>
              Suggested ({family.alert_counts?.orange || 0})
            </span>
          ) : family.all_reviewed ? (
            <span className="badge bg-success">
              <i className="bi bi-check2-circle me-1"></i>
              Reviewed
            </span>
          ) : (
            <span className="text-muted small">No action needed</span>
          )}
        </td>
        <td className="text-center">
          <span className="badge bg-secondary">{family.reserve_count}</span>
        </td>
        <td className="text-end">{fmtCurrency(family.total_reserve_dollar)}</td>
        <td className="text-end">{fmtCurrency(family.total_cash_dollar)}</td>
        <td className="text-end">
          {family.total_outflows > 0 ? (
            <span className="text-danger fw-semibold">{fmtCurrency(family.total_outflows)}</span>
          ) : (
            <span className="text-muted">{fmtCurrency(0)}</span>
          )}
        </td>
        <td className="text-end">
          {missingValue === family.account_count ? (
            <span className="text-muted">—</span>
          ) : (
            <>
              {fmtCurrency(family.total_account_value)}
              {missingValue > 0 && (
                <span
                  className="text-muted"
                  title={`Excludes ${missingValue} account${missingValue === 1 ? '' : 's'} with no reported value`}
                >
                  *
                </span>
              )}
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="p-0" style={{ background: '#eef1f6' }}>
            <ConsolidatedPanel
              accounts={sortedAccounts}
              days={days}
              missingManagementFee={missingManagementFee}
              matchCount={matchCount}
              matchesFilters={matchesFilters}
              sortConfig={sortConfig}
              onSort={onSort}
              onReviewed={onReviewed}
              onIgnored={onIgnored}
              onAnnotationChanged={onAnnotationChanged}
              onApplied={onApplied}
              onCombined={onCombined}
              onAlertIgnoresChanged={onAlertIgnoresChanged}
              selectedSuggestedActions={selectedSuggestedActions}
              ignoreManagementFee={ignoreManagementFee}
              reloadToken={reloadToken}
            />
          </td>
        </tr>
      )}
    </>
  );
};

// The suggested-action filter survives page refreshes via localStorage (the
// same persistence the Rebalancer's collapsed-section state uses — see
// hooks/useRebalanceState.js). Per-browser on purpose: it's a personal view
// preference, unlike the firm-wide ignore list, which lives server-side.
const SUGGESTED_ACTION_FILTER_KEY = 'cashReservesSelectedSuggestedActions';

const readSavedSuggestedActionFilter = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SUGGESTED_ACTION_FILTER_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((value) => typeof value === 'string') : [];
  } catch (err) {
    return [];
  }
};

// Page tabs. The active one lives in ?tab= so a link (or bookmark) can open
// the RMD Clients list directly.
const TABS = [
  { key: 'reserves', label: 'Reserve Accounts', icon: 'bi-list-ul' },
  { key: 'rmd', label: 'RMD Clients', icon: 'bi-calendar-check' },
];

// ─── main component ───────────────────────────────────────────────────────────
const CashReservesDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'rmd' ? 'rmd' : 'reserves';
  // The RMD tab mounts on first visit and stays mounted, so switching back and
  // forth doesn't refetch its report.
  const [rmdVisited, setRmdVisited] = useState(activeTab === 'rmd');
  useEffect(() => {
    if (activeTab === 'rmd') setRmdVisited(true);
  }, [activeTab]);
  const selectTab = (key) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (key === 'reserves') next.delete('tab');
        else next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  };
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // One expansion set, always keyed by family_id. Ungrouped rows are
  // single-account families keyed "acct:{number}", so no code path holds a bare
  // account number and the two key spaces can never be mixed.
  const [expanded, setExpanded] = useState(new Set());
  const [days] = useState(DEFAULT_DAYS);
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(true);
  const [ignoreManagementFee, setIgnoreManagementFee] = useState(false);
  const [onlyNonRecurring, setOnlyNonRecurring] = useState(false);
  const [onlyMissingManagementFee, setOnlyMissingManagementFee] = useState(false);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [selectedSuggestedActions, setSelectedSuggestedActions] = useState(
    readSavedSuggestedActionFilter
  );
  const [groupByFamily, setGroupByFamily] = useState(true);
  const [showIgnoreManager, setShowIgnoreManager] = useState(false);
  const [showRequestQueue, setShowRequestQueue] = useState(false);
  // Reserves requested but not yet confirmed in Tamarac. The rows are kept, not
  // just the count, so opening the modal can paint from them instead of
  // refetching what this page already has.
  const [openRequests, setOpenRequests] = useState(null);
  const [showTodoList, setShowTodoList] = useState(false);
  const [showHiddenSuggestions, setShowHiddenSuggestions] = useState(false);
  // Dashboard actions awaiting their mirror in Tamarac — same treatment as the
  // request queue: rows kept so the modal paints without refetching.
  const [openTodos, setOpenTodos] = useState(null);
  // Suggested-action types ignored firm-wide (backend drops their alerts).
  const [alertIgnores, setAlertIgnores] = useState([]);
  // Bumped when an annotation changes outside a panel, so an open panel
  // re-evaluates its cache (the changed account having been invalidated).
  const [txReloadToken, setTxReloadToken] = useState(0);
  // Column sort: null key falls back to the default (needs-attention-first)
  // ordering. Clicking a header sets the column; clicking it again toggles
  // ascending/descending. The account config drives every Accounts subtable, so
  // the merged sections below it group in the same order.
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [familySortConfig, setFamilySortConfig] = useState({ key: null, direction: 'asc' });

  const makeSortHandler = (setter) => (key) =>
    setter((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  const handleAccountSort = makeSortHandler(setSortConfig);
  const handleFamilySort = makeSortHandler(setFamilySortConfig);

  // Default loads serve the precomputed snapshot (fast); the Refresh button
  // passes { refresh: true } to force a live recompute.
  //
  // `silent` skips the loading state. Alerts are computed server-side, so every
  // mutation has to reload this payload to see its effect — and showing the
  // spinner for that would swap the whole table out, remounting every expanded
  // panel and discarding the account-selection filter and any open editor. Only
  // the first load and the Refresh button are worth a spinner.
  const loadAccounts = useCallback(async ({ refresh = false, silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      // Only a full recompute invalidates every account's transactions. Routine
      // mutations invalidate the one account they touched, so reloading after
      // Mark Reviewed doesn't refetch a whole family.
      // A refresh may follow an ingest that minted new snapshots, so the
      // fee-snapshot cache goes with the transaction cache.
      if (refresh) {
        clearTxCache();
        clearFeeSnapshotCache();
        // The account/category suggestions come from the same report the
        // recompute re-reads, so they go stale on exactly the same schedule.
        clearReserveRequestOptionsCache();
      }
      const data = await cashReservesService.getReserveAccounts({ refresh });
      setAccounts(data.accounts || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load reserve accounts');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // The badge has to be right before anyone opens the modal, so the count is
  // fetched on load and refreshed whenever the queue changes. A queue that
  // can't be read is not worth an error banner over the whole dashboard — the
  // modal reports the failure properly if someone actually opens it.
  const loadRequestQueue = useCallback(async () => {
    try {
      const data = await cashReservesService.getReserveRequests({ status: 'open' });
      setOpenRequests(data.requests || []);
    } catch (err) {
      console.error('Could not load the reserve request queue:', err);
    }
  }, []);

  useEffect(() => {
    loadRequestQueue();
  }, [loadRequestQueue]);

  // Same contract as the request-queue badge: fetched on load, refreshed when
  // the list changes, and a failure is the modal's to report, not the page's.
  const loadTodos = useCallback(async () => {
    try {
      const data = await cashReservesService.getTamaracTodos({ status: 'open' });
      setOpenTodos(data.todos || []);
    } catch (err) {
      console.error('Could not load the Tamarac todo list:', err);
    }
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  // Ignored suggestion types, for the "Hidden suggestions" dropdown. Same
  // failure posture as the other toolbar badges: a list that can't load is
  // not worth an error banner over the whole dashboard.
  const loadAlertIgnores = useCallback(async () => {
    try {
      const data = await cashReservesService.getAlertIgnores();
      setAlertIgnores(data.ignores || []);
    } catch (err) {
      console.error('Could not load ignored suggestion types:', err);
    }
  }, []);

  useEffect(() => {
    loadAlertIgnores();
  }, [loadAlertIgnores]);

  // Something changed that only alerts / review state reflect, and whoever
  // changed it has already refreshed whatever else it affected. Marking an
  // account reviewed is what mints Tamarac todos server-side, so the todo
  // badge reloads alongside the accounts.
  const reloadSilently = useCallback(() => {
    loadAccounts({ silent: true });
    loadTodos();
  }, [loadAccounts, loadTodos]);

  // Hiding/restoring a suggestion type recomputes alerts server-side, so the
  // accounts reload alongside the dropdown's own list.
  const handleAlertIgnoresChanged = useCallback(() => {
    loadAlertIgnores();
    reloadSilently();
  }, [loadAlertIgnores, reloadSilently]);

  // An annotation changed from OUTSIDE the transactions panel (a suggestion
  // applied, reserves combined), so that account's cached transactions are
  // stale and open panels need nudging as well as the alerts reloading.
  const handleAccountMutated = useCallback(
    (accountNumber) => {
      if (accountNumber) invalidateTxCache(accountNumber);
      setTxReloadToken((token) => token + 1);
      loadAccounts({ silent: true });
    },
    [loadAccounts]
  );

  const toggleRow = (familyId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };

  // Base working set: when ignoring Management Fee reserves, drop accounts whose
  // only reserve is a Management Fee and subtract the Management Fee from the
  // reserve target of the accounts that remain. Search and the needs-attention
  // toggle filter this set.
  const baseAccounts = useMemo(() => {
    // Always drop the firm's own (Halter Ferguson Financial) accounts.
    let working = accounts.filter((a) => !isFirmAccount(a));
    if (ignoreManagementFee) {
      working = working
        .filter((a) => !isManagementFeeOnly(a))
        .map((a) => {
          const fee = managementFeeDollars(a);
          if (fee <= 0) return a;
          return {
            ...a,
            total_reserve_dollar: Math.round(((a.total_reserve_dollar || 0) - fee) * 100) / 100,
          };
        });
    }
    return working;
  }, [accounts, ignoreManagementFee]);

  // Family ids that still have an unresolved management-fee warning because at
  // least one account in the family is missing the reserve and is not waived.
  // Derived from raw accounts (minus the firm's own) so this signal is stable
  // and unaffected by display filters.
  const familiesWithMissingManagementFee = useMemo(() => {
    const ids = new Set();
    accounts
      .filter((a) => !isFirmAccount(a) && accountMissingManagementFee(a))
      .forEach((a) => ids.add(familyIdOf(a)));
    return ids;
  }, [accounts]);

  const suggestedActionOptions = useMemo(() => {
    const byAction = new Map();
    baseAccounts.forEach((account) => {
      (account.alerts || []).forEach((alert) => {
        if (!alert?.action || isRecurringFlagAlert(alert) || byAction.has(alert.action)) return;
        byAction.set(alert.action, alert.label || alert.action);
      });
    });
    return Array.from(byAction.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [baseAccounts]);

  const selectedSuggestedActionsSet = useMemo(
    () => new Set(selectedSuggestedActions),
    [selectedSuggestedActions]
  );

  const selectedSuggestedActionLabels = useMemo(() => {
    const labelsByValue = new Map(suggestedActionOptions.map((option) => [option.value, option.label]));
    return selectedSuggestedActions
      .map((value) => labelsByValue.get(value) || value)
      .filter(Boolean);
  }, [selectedSuggestedActions, suggestedActionOptions]);

  const toggleSuggestedAction = useCallback((value) => {
    setSelectedSuggestedActions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const matchesSelectedSuggestedAction = useCallback(
    (account) => {
      if (selectedSuggestedActionsSet.size === 0) return true;
      return (account.alerts || []).some(
        (alert) => !isRecurringFlagAlert(alert) && selectedSuggestedActionsSet.has(alert.action)
      );
    },
    [selectedSuggestedActionsSet]
  );

  useEffect(() => {
    // Before the accounts arrive the option list is empty — pruning then would
    // wipe the selection restored from localStorage, so wait for real options.
    if (suggestedActionOptions.length === 0) return;
    const optionValues = new Set(suggestedActionOptions.map((option) => option.value));
    setSelectedSuggestedActions((prev) => prev.filter((value) => optionValues.has(value)));
  }, [suggestedActionOptions]);

  // Persist every change (including clearing) so a refresh resumes the view.
  useEffect(() => {
    try {
      localStorage.setItem(
        SUGGESTED_ACTION_FILTER_KEY,
        JSON.stringify(selectedSuggestedActions)
      );
    } catch (err) {
      // Storage full/unavailable (private mode) — the filter still works for
      // this session, it just won't survive a refresh.
    }
  }, [selectedSuggestedActions]);

  // Does one account pass the account-level filters? A family is kept when any
  // of its accounts match, so the panel uses this to dim the rest rather than
  // hide them — they still contribute to the family's totals.
  const matchesFilters = useCallback(
    (a) => {
      const term = searchTerm.trim().toLowerCase();
      if (onlyNeedsAttention && !showsNeedsAttention(a)) return false;
      if (onlyNonRecurring && !hasNonRecurringReserve(a)) return false;
      if (onlyMissingManagementFee && !familiesWithMissingManagementFee.has(familyIdOf(a))) return false;
      if (hideReviewed && a.review_status === 'reviewed') return false;
      if (!matchesSelectedSuggestedAction(a)) return false;
      if (term && !`${a.account_name} ${a.account_number}`.toLowerCase().includes(term)) return false;
      return true;
    },
    [
      searchTerm,
      onlyNeedsAttention,
      onlyNonRecurring,
      onlyMissingManagementFee,
      familiesWithMissingManagementFee,
      hideReviewed,
      matchesSelectedSuggestedAction,
    ]
  );

  const totalReserve = useMemo(
    () => baseAccounts.reduce((sum, a) => sum + (a.total_reserve_dollar || 0), 0),
    [baseAccounts]
  );

  const needsAttentionCount = useMemo(
    () => baseAccounts.filter(showsNeedsAttention).length,
    [baseAccounts]
  );

  // Narrows the "Needs Attention" number to the suggested-action filter alone
  // (not the other toolbar filters), so the card can show "2 of 312" without
  // conflating it with search/family/review filters that already hide rows.
  const needsAttentionFilteredCount = useMemo(
    () =>
      selectedSuggestedActionsSet.size === 0
        ? null
        : baseAccounts.filter((a) => showsNeedsAttention(a) && matchesSelectedSuggestedAction(a)).length,
    [baseAccounts, selectedSuggestedActionsSet, matchesSelectedSuggestedAction]
  );

  // Tracked separately from Needs Attention so a stale review with no actual
  // alert doesn't inflate that count — still surfaced, just not as "action".
  const needsRereviewOnlyCount = useMemo(
    () => baseAccounts.filter((a) => showsNeedsRereview(a) && !showsNeedsAttention(a)).length,
    [baseAccounts]
  );

  // The rows the table renders. Grouped mode groups by rebalancing group;
  // ungrouped mode makes every account its own single-account family, so the
  // row and detail components are identical in both.
  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = buildFamilies(baseAccounts, { grouped: groupByFamily })
      .map((family) => ({
        ...family,
        missing_management_fee: familiesWithMissingManagementFee.has(
          groupByFamily ? family.family_id : familyIdOf(family.accounts[0])
        ),
        match_count: family.accounts.filter(matchesFilters).length,
      }))
      .filter((family) => {
        if (onlyNeedsAttention && family.needs_attention_count === 0) return false;
        if (onlyNonRecurring && !family.has_non_recurring) return false;
        if (onlyMissingManagementFee && !family.missing_management_fee) return false;
        // Hide a family once nothing needs attention AND at least one of its
        // accounts has been reviewed — so leftover "no action needed" accounts
        // (unreviewed, no outflows) don't keep a handled family on screen.
        if (hideReviewed && family.needs_attention_count === 0 && family.has_reviewed) return false;
        if (!family.accounts.some(matchesSelectedSuggestedAction)) return false;
        if (term) {
          const matches =
            family.family_full_name.toLowerCase().includes(term) ||
            family.accounts.some((a) =>
              `${a.account_name} ${a.account_number}`.toLowerCase().includes(term)
            );
          if (!matches) return false;
        }
        return true;
      });
    // Explicit column sort wins when a family header has been clicked.
    const columnSorted = sortByColumn(filtered, FAMILY_SORT_ACCESSORS, familySortConfig);
    if (columnSorted) return columnSorted;
    const reviewedRank = (f) => (f.all_reviewed ? 1 : 0);
    return filtered.sort(
      (a, b) =>
        reviewedRank(a) - reviewedRank(b) ||
        (b.needs_attention_count > 0 ? 1 : 0) - (a.needs_attention_count > 0 ? 1 : 0) ||
        (b.total_outflows || 0) - (a.total_outflows || 0)
    );
  }, [
    groupByFamily,
    baseAccounts,
    searchTerm,
    onlyNeedsAttention,
    onlyNonRecurring,
    onlyMissingManagementFee,
    hideReviewed,
    familySortConfig,
    familiesWithMissingManagementFee,
    matchesSelectedSuggestedAction,
    matchesFilters,
  ]);

  const shownAccountCount = useMemo(
    () => rows.reduce((sum, f) => sum + f.account_count, 0),
    [rows]
  );

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-stretch flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-0">
            <i className="bi bi-piggy-bank me-2 text-primary"></i>
            Cash Reserves
          </h3>
          <div className="text-muted small">
            Cash Reserves Monitoring Dashboard
            {lastUpdated && ` · updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>

        {activeTab === 'reserves' && (
          <>
            <div className="d-flex align-items-stretch gap-2 flex-grow-1" style={{ minWidth: '320px' }}>
              <div
                className="card text-center"
                style={{
                  borderLeft: '4px solid #6f42c1',
                  flex: '1 1 220px',
                }}
              >
                <div className="card-body py-2 px-3">
                  <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Accounts with a Reserve
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#6f42c1' }}>
                    {isLoading ? '—' : baseAccounts.length}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="card text-center p-0"
                style={{
                  borderLeft: '4px solid #dc3545',
                  flex: '1 1 220px',
                  cursor: 'pointer',
                  boxShadow: onlyNeedsAttention ? '0 0 0 2px #dc3545' : undefined,
                }}
                onClick={() => setOnlyNeedsAttention((v) => !v)}
                title="Click to filter to accounts with recent outflow activity"
                aria-pressed={onlyNeedsAttention}
              >
                <div className="card-body py-2 px-3">
                  <div className="d-flex align-items-center justify-content-center gap-1 text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Needs Attention
                    {onlyNeedsAttention && <i className="bi bi-funnel-fill text-danger" title="Filter active"></i>}
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#dc3545' }}>
                    {isLoading
                      ? '—'
                      : needsAttentionFilteredCount != null
                        ? `${needsAttentionFilteredCount} of ${needsAttentionCount}`
                        : needsAttentionCount}
                  </div>
                  {!isLoading && needsRereviewOnlyCount > 0 && (
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                      +{needsRereviewOnlyCount} pending re-review
                    </div>
                  )}
                </div>
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowHiddenSuggestions(true)}
                title="Suggested-action types hidden per account"
              >
                <i className="bi bi-eye-slash me-1"></i>
                Hidden Suggestions
                {alertIgnores.length > 0 && (
                  <span className="badge bg-primary ms-2">{alertIgnores.length}</span>
                )}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowTodoList(true)}
                title="Dashboard actions (notes, reserve links) that still need entering in Tamarac by hand"
              >
                <i className="bi bi-list-check me-1"></i>
                Tamarac Todo List
                {openTodos?.length > 0 && (
                  <span className="badge bg-primary ms-2">{openTodos.length}</span>
                )}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowRequestQueue(true)}
                title="Cash reserves that have been requested but may not be in Tamarac yet"
              >
                <i className="bi bi-clipboard-plus me-1"></i>
                Reserve Creation Queue
                {openRequests?.length > 0 && (
                  <span className="badge bg-primary ms-2">{openRequests.length}</span>
                )}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowIgnoreManager(true)}
                title="View and edit the shared list of accounts waived for the management-fee requirement"
              >
                <i className="bi bi-eye-slash me-1"></i>
                Manage Mgmt Fees Warnings
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => loadAccounts({ refresh: true })}
                disabled={isLoading}
                title="Re-run the analysis now instead of using the daily precomputed snapshot"
              >
                <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                Refresh
              </button>
            </div>
          </>
        )}
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => selectTab(tab.key)}
              aria-current={activeTab === tab.key ? 'page' : undefined}
            >
              <i className={`bi ${tab.icon} me-1`}></i>
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {activeTab === 'reserves' && (
        <>
          {/* Error banner */}
          {error && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
            </div>
          )}

          {/* Accounts table */}
          <div className="card shadow-sm">
            <div className="card-header d-flex align-items-center flex-wrap gap-3">
              <span className="fw-semibold">
                <i className="bi bi-list-ul me-2 text-primary"></i>
                Reserve Accounts
                {expanded.size > 0 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-3 text-decoration-none"
                    onClick={() => setExpanded(new Set())}
                  >
                    Collapse all
                  </button>
                )}
              </span>
              <div className="d-flex align-items-center gap-2 flex-nowrap">
                <div style={{ maxWidth: '320px', width: '100%' }}>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search name or account #"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className={`btn btn-sm ${onlyNeedsAttention ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setOnlyNeedsAttention(!onlyNeedsAttention)}
                  title="Only show accounts needing attention"
                  aria-pressed={onlyNeedsAttention}
                >
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${onlyMissingManagementFee ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setOnlyMissingManagementFee(!onlyMissingManagementFee)}
                  title="Only show account families missing a Management Fee reserve"
                  aria-pressed={onlyMissingManagementFee}
                >
                  <i className="bi bi-cash-coin"></i>
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${hideReviewed ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setHideReviewed(!hideReviewed)}
                  title="Hide accounts whose review is up to date"
                  aria-pressed={hideReviewed}
                >
                  <i className="bi bi-eye-slash-fill"></i>
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${groupByFamily ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    // Row keys differ between the two modes, so collapse
                    // everything rather than leave stale ids expanded.
                    setExpanded(new Set());
                    setGroupByFamily(!groupByFamily);
                  }}
                  title="Group accounts into families by Tamarac rebalancing group"
                  aria-pressed={groupByFamily}
                >
                  <i className="bi bi-people-fill"></i>
                </button>
                <div className="d-flex align-items-center gap-1 flex-nowrap">
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      type="button"
                      id="suggestedActionsFilter"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                      aria-expanded="false"
                      title={
                        selectedSuggestedActionLabels.length > 0
                          ? selectedSuggestedActionLabels.join(', ')
                          : 'Filter by suggested action'
                      }
                    >
                      <i className="bi bi-lightbulb-fill me-1"></i>
                      {selectedSuggestedActions.length === 0
                        ? 'Filter Actions'
                        : `${selectedSuggestedActions.length} selected`}
                    </button>
                    <div
                      className="dropdown-menu p-2"
                      aria-labelledby="suggestedActionsFilter"
                      style={{ minWidth: '320px', maxHeight: '16rem', overflowY: 'auto' }}
                    >
                      {suggestedActionOptions.length === 0 ? (
                        <div className="text-muted small px-1 py-1">No suggested actions available</div>
                      ) : (
                        suggestedActionOptions.map((option) => (
                          <label key={option.value} className="dropdown-item d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={selectedSuggestedActionsSet.has(option.value)}
                              onChange={() => toggleSuggestedAction(option.value)}
                            />
                            <span className="small">{option.label}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  {selectedSuggestedActions.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      onClick={() => setSelectedSuggestedActions([])}
                      title="Clear suggested action filter"
                    >
                      <i className="bi bi-x-circle"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              {isLoading ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border text-primary mb-2" role="status" />
                  <div>Loading reserve accounts…</div>
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
                  <div className="mt-2">
                    {accounts.length === 0
                      ? 'No accounts have a configured cash reserve.'
                      : baseAccounts.length === 0
                        ? 'All reserve accounts are Management Fee-only.'
                        : groupByFamily
                          ? 'No families match your filters.'
                          : 'No accounts match your filters.'}
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <SortableHeader
                          label={groupByFamily ? 'Account Family' : 'Account Name'}
                          sortKey="family_full_name"
                          sortConfig={familySortConfig}
                          onSort={handleFamilySort}
                        />
                        <SortableHeader label="Accounts" sortKey="account_count" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-center" />
                        <SortableHeader label="Status" sortKey="status" sortConfig={familySortConfig} onSort={handleFamilySort} />
                        <SortableHeader label="Reserves" sortKey="reserve_count" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-center" />
                        <SortableHeader label="Reserve Target" sortKey="total_reserve_dollar" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-end" />
                        <SortableHeader label="Total Cash" sortKey="total_cash_dollar" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-end" />
                        <SortableHeader label={`Outflows (${days}d)`} sortKey="total_outflows" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-end" />
                        <SortableHeader label="Account Value" sortKey="total_account_value" sortConfig={familySortConfig} onSort={handleFamilySort} className="text-end" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((family) => (
                        <FamilyRow
                          key={family.family_id}
                          family={family}
                          expanded={expanded.has(family.family_id)}
                          onToggle={() => toggleRow(family.family_id)}
                          days={days}
                          missingManagementFee={family.missing_management_fee}
                          matchCount={family.match_count}
                          matchesFilters={matchesFilters}
                          sortConfig={sortConfig}
                          onSort={handleAccountSort}
                          onReviewed={reloadSilently}
                          onIgnored={reloadSilently}
                          onAnnotationChanged={reloadSilently}
                          onApplied={handleAccountMutated}
                          onCombined={handleAccountMutated}
                          onAlertIgnoresChanged={handleAlertIgnoresChanged}
                          selectedSuggestedActions={selectedSuggestedActions}
                          ignoreManagementFee={ignoreManagementFee}
                          reloadToken={txReloadToken}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {!isLoading && rows.length > 0 && (
              <div className="card-footer text-muted small">
                {groupByFamily
                  ? `Showing ${rows.length} account ${rows.length !== 1 ? 'families' : 'family'} (${shownAccountCount} account${shownAccountCount !== 1 ? 's' : ''})`
                  : `Showing ${rows.length} of ${baseAccounts.length} account${baseAccounts.length !== 1 ? 's' : ''}${ignoreManagementFee ? ' (Management Fee-only hidden)' : ''}`}
                . Click a row to view its reserves, suggested actions and recent transactions.
              </div>
            )}
          </div>
        </>
      )}

      {rmdVisited && (
        <div hidden={activeTab !== 'rmd'}>
          <RmdClientsTab />
        </div>
      )}

      {showIgnoreManager && (
        <IgnoreListManager
          onClose={() => setShowIgnoreManager(false)}
          onChanged={reloadSilently}
        />
      )}

      {showRequestQueue && (
        <ReserveRequestQueue
          onClose={() => setShowRequestQueue(false)}
          onChanged={loadRequestQueue}
          initialRequests={openRequests}
        />
      )}

      {showTodoList && (
        <TamaracTodoList
          onClose={() => setShowTodoList(false)}
          onChanged={loadTodos}
          initialTodos={openTodos}
        />
      )}

      {showHiddenSuggestions && (
        <HiddenSuggestionsModal
          ignores={alertIgnores}
          onClose={() => setShowHiddenSuggestions(false)}
          onChanged={handleAlertIgnoresChanged}
        />
      )}

      <style>{`
        .spin { animation: cash-reserve-spin 1s linear infinite; }
        @keyframes cash-reserve-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cash-reserve-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          z-index: 1050;
        }
      `}</style>
    </div>
  );
};

export default CashReservesDashboard;
